import { capitalize } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { CardSelect } from '~/eds/src/components/CardSelect/CardSelect';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { FullPageOverlay } from '~/eds/src/components/FullPageOverlay/FullPageOverlay';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { Text } from '~/eds/src/components/Text/Text';
import { DEFAULT_POLL_INTERVAL_MS } from '~/eds/src/constants/duration';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { PUBLISH_ELEMENT_ID } from '../../constants';
import { MODEL_CART_WIDTH } from '../constants';
import { useGetModelLibrarySteps } from '../hooks';
import { ModelCart } from '../ModelLibrary/ModelCart';
import { ModelPreview } from '../ModelLibrary/ModelPreview';
import { LibraryModelVersion } from '../types';
import { CategoryScope } from './CategoryScope';
import { GlobalScope } from './GlobalScope';
import { ModelPublishStates } from './ModelPublishStates';
import {
  getPublishingModalTitle,
  updateAllModelsCustomQuery,
  updateAllModelsGlobalQuery,
} from './utils';

type Callback = () => void;

type ScopeType = 'global' | 'custom';

interface Props {
  onBack: Callback;
  onCancel: Callback;
}

export const ModelAddScope = ({ onBack, onCancel }: Props) => {
  const intl = useIntl();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const { toast } = useToast();
  const trackSegment = useTrackSegment();
  const user = useCurrentUser();

  const [scopeType, setScopeType] = useState<ScopeType>('global');
  const [isDraft, setIsDraft] = useState(true);
  const [enablePolling, setEnablePolling] = useState(false);
  // For display in the publishing modal
  const [publishingModels, setPublishingModels] = useState<
    LibraryModelVersion[]
  >([]);
  // To track test case creation status
  const [publishedPromptModels, setPublishedPromptModels] = useState<
    LibraryModelVersion[]
  >([]);

  const step = useSelector(slices.xRayLibrary.selectors.selectStep);

  const query = useSelector(slices.xRayLibrary.selectors.selectQuery);

  const modelsInCart = useSelector(slices.xRayLibrary.selectors.selectCart);

  const categoryModels = useSelector(
    slices.xRayLibrary.selectors.selectCategoryModels,
  );

  const isCartVisible = useSelector(
    slices.xRayLibrary.selectors.selectIsCartVisible,
  );

  const cartStatusMessage = useSelector(
    slices.xRayLibrary.selectors.selectCartStatusMessage,
  );

  const isSingleCategory = [...categoryModels.keys()].length === 1;

  const modelLibrarySteps = useGetModelLibrarySteps(intl);

  const modelCountText = intl.formatMessage(
    {
      id: 'ModelAddScope.modelCount',
      defaultMessage: '{count} {count, plural, one {model} other {models}}',
      description: 'Count of models with pluralization. Example: count = 5',
    },
    {
      count: formatNumber(modelsInCart.length),
    },
  );

  const [publishLibraryModels, { isLoading: isPublishing }] =
    api.endpoints.publishLibraryModels.useMutation();

  const [createTestCase] =
    api.endpoints.createPromptModelTestCase.useMutation();

  const { data: publishingModelsData = [] } =
    api.endpoints.getLibraryModelsPublishStatus.useQuery(
      {},
      { pollingInterval: DEFAULT_POLL_INTERVAL_MS, skip: !enablePolling },
    );

  const isCreatingTestCases = Boolean(
    publishedPromptModels.length &&
    publishedPromptModels.some((model) => model.testCaseState === 'pending'),
  );

  const isPublishComplete = Boolean(
    publishedPromptModels.length &&
    publishedPromptModels.every(
      (model) =>
        model.testCaseState === 'done' || model.testCaseState === 'error',
    ),
  );

  const scopes = useMemo(() => {
    return scopeType === 'global'
      ? updateAllModelsGlobalQuery({
          globalQuery: query.globalQuery,
          modelsInCart,
        })
      : updateAllModelsCustomQuery({
          categoryQuery: query.categoryQuery,
          categoryModels,
        });
  }, [scopeType, query]);

  const isScopeEmpty =
    !scopes ||
    !Object.values(scopes).every((value) => {
      // Check if value is an array and has items
      return Array.isArray(value) && value.length > 0;
    });

  const disablePublish =
    modelsInCart.length === 0 ||
    isPublishing ||
    enablePolling ||
    isCreatingTestCases ||
    isScopeEmpty;

  const actionMode = isDraft
    ? intl.formatMessage({
        id: 'ModelAddScope.actionModeSaving',
        defaultMessage: 'saving',
        description: 'Action mode for saving models as draft',
      })
    : intl.formatMessage({
        id: 'ModelAddScope.publishing',
        defaultMessage: 'publishing',
        description: 'Action mode for publishing models',
      });

  const publishingTooltip =
    modelsInCart.length === 0
      ? intl.formatMessage({
          id: 'ModelAddScope.noModelsInCart',
          defaultMessage: 'No models selected.',
          description: 'Tooltip when no models are selected.',
        })
      : disablePublish && !isScopeEmpty
        ? intl.formatMessage(
            {
              id: 'ModelAddScope.stillProcessingPlural',
              defaultMessage:
                '{modelText} {count, plural, one {is} other {are}} still {actionMode}…',
              description:
                'Tooltip when models are still being processed with proper pluralization. Example: modelText = "example value", count, plural, one {is = "5", are = "example value", actionMode = "moved", count = "5"',
            },
            {
              modelText: capitalize(modelCountText),
              actionMode,
              count: modelsInCart.length,
            },
          )
        : isScopeEmpty
          ? intl.formatMessage({
              id: 'ModelAddScope.setScopeFirst',
              defaultMessage:
                'Please set a scope for the models before publishing.',
              description: 'Tooltip when no scope is set',
            })
          : undefined;

  const [confirmPublish, showConfirmPublish, hideConfirmPublish] = useModal({
    title: intl.formatMessage({
      id: 'ModelAddScope.confirmPublishTitle',
      defaultMessage: 'Are you sure you want to publish?',
      description: 'Title for publish confirmation modal',
    }),
    children: (
      <Layout id={PUBLISH_ELEMENT_ID} preset="sections">
        <Section
          title={intl.formatMessage({
            id: 'ModelAddScope.publishModelSection',
            defaultMessage: 'Publish model',
            description: 'Section title for publish model',
          })}
        >
          <Text>
            {intl.formatMessage(
              {
                id: 'ModelAddScope.quotaWarning',
                defaultMessage:
                  "You're about to publish {modelText}. This action will affect your account balance.",
                description:
                  'Warning message shown to the user indicating that publishing the model will consume model credits or quota.',
              },
              {
                modelText: modelCountText,
              },
            )}
          </Text>
        </Section>
      </Layout>
    ),
    primaryAction: {
      isLoading: isPublishing,
      text: isPublishing
        ? intl.formatMessage({
            id: 'ModelAddScope.publishing1',
            defaultMessage: 'Publishing…',
            description: 'Button text while publishing',
          })
        : intl.formatMessage({
            id: 'ModelAddScope.publish2',
            defaultMessage: 'Publish',
            description: 'Button text for publish action',
          }),
      onClick: () => handlePublish(),
    },
  });

  const [confirmDraft, showConfirmDraft, hideConfirmDraft] = useModal({
    title: intl.formatMessage({
      id: 'ModelAddScope.saveAndTestModalTitle',
      defaultMessage: 'Save and test',
      description:
        'Modal title for confirming the action to save models as drafts',
    }),
    children: (
      <Text>
        {intl.formatMessage(
          {
            id: 'ModelAddScope.saveAndTestModalDescription',
            defaultMessage:
              'Save {count} {count, plural, one {model} other {models}} as {count, plural, one {a draft} other {drafts}} to evaluate before publishing. You can test and make adjustments to {count, plural, one {this} other {these}} {count, plural, one {model} other {models}} from the Automation Hub.',
            description:
              'Description for saving models as drafts with pluralization. Example: count = "5", count, plural, one {model = "5", models = "example value", count, plural, one {a draft = "5", drafts = "example value", count, plural, one {this = "5", these = "example value"',
          },
          {
            count: modelsInCart.length,
          },
        )}
      </Text>
    ),
    primaryAction: {
      isLoading: isPublishing,
      text: isPublishing
        ? intl.formatMessage({
            id: 'ModelAddScope.confirmDraftSaving',
            defaultMessage: 'Saving…',
            description: 'Button text while saving',
          })
        : intl.formatMessage({
            id: 'ModelAddScope.confirmSave',
            defaultMessage: 'Save',
            description:
              'Button text for confirming the action to save models as drafts',
          }),
      onClick: () => handlePublish(),
    },
  });

  const allModelsFailed = publishingModels.every(
    (model) => model.publishState === 'error',
  );

  const handleNavigate = () => {
    dispatch(slices.xRayLibrary.actions.clearCart());
    dispatch(slices.xRayLibrary.actions.setStep('model-library'));
    if (publishingModels.length === 1 && publishingModels[0].promptModelId) {
      navigate(
        `${RoutePathType.AutomationHubFields}/${publishingModels[0].promptModelId}`,
      );
    } else {
      navigate(RoutePathType.AutomationHubFields);
    }
  };

  const [publishingModal, showPublishingModal] = useModal({
    cancelText: intl.formatMessage({
      id: 'ModelAddScope.publishingModal.close',
      defaultMessage: 'Close',
      description: 'Button text to close the modal',
    }),
    children: (
      <ModelPublishStates
        isDraft={isDraft}
        models={publishingModels.map((model) => ({
          ...model,
          testCaseState: publishedPromptModels.find(
            (pm) => pm.promptModelId === model.promptModelId,
          )?.testCaseState,
        }))}
      />
    ),
    disableHideOnEscape: true,
    primaryAction: allModelsFailed
      ? undefined
      : {
          disabled: disablePublish,
          tooltip: publishingTooltip,
          text: intl.formatMessage(
            {
              id: 'ModelAddScope.viewModels',
              defaultMessage:
                'View {count, plural, one {model} other {models}}',
              description:
                'Button text to view models with pluralization. Example: count = 4',
            },
            {
              count: modelsInCart.length,
            },
          ),
          onClick: handleNavigate,
        },
    title: getPublishingModalTitle({
      intl,
      isDraft,
      modelsInCartCount: modelsInCart.length,
      allModelsFailed,
      isCreatingTestCases,
      publishingModels,
    }),
    onHide: () => {
      dispatch(slices.xRayLibrary.actions.clearCart());
      onBack();
    },
  });

  const handlePublish = () => {
    publishLibraryModels({
      models: modelsInCart,
      scopes,
      isDraft,
    })
      .unwrap()
      .then((publishModels) => {
        if (publishModels?.length) {
          setEnablePolling(true);
          setPublishingModels(publishModels);
          isDraft ? hideConfirmDraft() : hideConfirmPublish();
          showPublishingModal();
          const trackingProps = {
            models: publishModels.map((model) => ({
              model: model.label,
              modelId: model.libraryModelId,
            })),
            userRole: user.role_uuid?.name ?? user.role,
          };
          isDraft
            ? trackSegment(
                'Library model(s) saved as draft (tested)',
                trackingProps,
              )
            : trackSegment(
                'Library model(s) published (purchased)',
                trackingProps,
              );
        }
      })
      .catch(() => {
        toast({
          message: isDraft
            ? intl.formatMessage(
                {
                  id: 'ModelAddScope.saveError',
                  defaultMessage:
                    'There was an error saving the {count, plural, one {model} other {models}}. Please try again.',
                  description:
                    'Error message when save fails with pluralization. Example: count = 5',
                },
                {
                  count: modelsInCart.length,
                },
              )
            : intl.formatMessage(
                {
                  id: 'ModelAddScope.publishError',
                  defaultMessage:
                    'There was an error publishing the {count, plural, one {model} other {models}}. Please try again.',
                  description:
                    'Error message when publish fails with pluralization. Example: count = 5',
                },
                {
                  count: modelsInCart.length,
                },
              ),
          status: 'danger',
        });
      });
  };

  useEffect(() => {
    if (isScopeEmpty) {
      dispatch(slices.xRayLibrary.actions.setStep('add-scope'));
    } else {
      dispatch(slices.xRayLibrary.actions.setStep('confirm-publish'));
    }
  }, [isScopeEmpty]);

  useEffect(() => {
    if (publishingModelsData.length) {
      const models = publishingModelsData.map((modelPublish) => {
        const model = modelsInCart.find(
          (model) => model.libraryModelId === modelPublish.libraryModelId,
        );
        return {
          ...model,
          ...modelPublish,
        } as LibraryModelVersion;
      });
      setPublishingModels(models);

      // Mark the newly published models for test case creation
      const newlyPublishedModels = models
        .filter(
          (model) =>
            model.publishState === 'done' &&
            !publishedPromptModels.find(
              (pm) => pm.promptModelId === model.promptModelId,
            ),
        )
        .map(
          (model) =>
            ({
              ...model,
              testCaseState: 'pending',
            }) as LibraryModelVersion,
        );

      if (newlyPublishedModels.length) {
        setPublishedPromptModels((prev) => [...newlyPublishedModels, ...prev]);
        newlyPublishedModels.forEach((model) => {
          if (model.promptModelId) {
            createTestCase({
              modelId: model.promptModelId,
              version: 1,
              highlightMethod,
            })
              .unwrap()
              .then(
                // on success
                (testCase) => {
                  if (testCase) {
                    setPublishedPromptModels((prev) => {
                      const updatedPromptModelInfos = prev.map((prevInfo) => {
                        if (prevInfo.promptModelId === model.promptModelId) {
                          return {
                            ...prevInfo,
                            testCaseState: 'done' as const,
                          };
                        }
                        return prevInfo;
                      });
                      return updatedPromptModelInfos;
                    });
                  }
                },
                // on error
                () => {
                  setPublishedPromptModels((prev) => {
                    const updatedPromptModelInfos = prev.map((prevInfo) => {
                      if (prevInfo.promptModelId === model.promptModelId) {
                        return { ...prevInfo, testCaseState: 'error' as const };
                      }
                      return prevInfo;
                    });
                    return updatedPromptModelInfos;
                  });
                },
              );
          }
        });
      }

      // If all models have finished publishing, stop polling
      if (
        publishingModelsData.every((model) => model.publishState !== 'pending')
      ) {
        setEnablePolling(false);
      }
    }
  }, [publishingModelsData]);

  return (
    <FullPageOverlay
      actions={[
        {
          disabled: disablePublish,
          level: 'secondary',
          tooltip: publishingTooltip,
          text: intl.formatMessage({
            id: 'ModelAddScope.saveAndTestButtonText',
            defaultMessage: 'Save & Test',
            description: 'Button text for saving models as drafts',
          }),
          onClick: () => {
            setIsDraft(true);
            showConfirmDraft();
          },
        },
        {
          disabled: disablePublish,
          level: 'primary',
          tooltip: publishingTooltip,
          text: intl.formatMessage({
            id: 'ModelAddScope.confirmAndPublish',
            defaultMessage: 'Confirm & Publish',
            description: 'Primary action button text',
          }),
          onClick: () => {
            setIsDraft(false);
            showConfirmPublish();
          },
        },
      ]}
      backAction={{
        text: intl.formatMessage({
          id: 'ModelAddScope.back',
          defaultMessage: 'Back',
          description: 'Back button text',
        }),
        onClick: onBack,
      }}
      backgroundGradient="library"
      cancelAction={{
        text: intl.formatMessage({
          id: 'ModelLibrary.close',
          defaultMessage: 'Close',
          description: 'Close button text',
        }),
        level: 'tertiary',
        onClick: onCancel,
      }}
      headerCalloutContent={
        <Actions
          actions={[
            {
              count: modelsInCart.length,
              level: 'cta',
              mode: 'chip',
              text: intl.formatMessage({
                id: 'ModelLibrary.viewSelectedModels',
                defaultMessage: 'View Selected Models',
                description:
                  'Button text showing selected models count. Example: count = 5',
              }),
              onClick: () => {
                dispatch(slices.xRayLibrary.actions.toggleIsCartVisible());
                if (isCartVisible) {
                  dispatch(slices.xRayLibrary.actions.clearCartStatusMessage());
                }
              },
            },
          ]}
        />
      }
      panel={
        isCartVisible
          ? {
              cancelAction: {
                text: intl.formatMessage({
                  id: 'Panel.closeButton',
                  defaultMessage: 'Close',
                  description: 'Button label for closing the panel',
                }),
                onClick: () => {
                  dispatch(slices.xRayLibrary.actions.toggleIsCartVisible());
                  dispatch(slices.xRayLibrary.actions.clearCartStatusMessage());
                },
              },
              children: <ModelCart />,
              width: MODEL_CART_WIDTH,
              enableContentPadding: false,
              position: 'right',
              mode: 'inline',
              statusMessage: cartStatusMessage ?? undefined,
              title: intl.formatMessage({
                id: 'ModelCart.selectedModels',
                defaultMessage: 'Selected Models',
                description: 'Title for selected models panel',
              }),
            }
          : undefined
      }
      stepper={{
        currentStepId: step,
        isOrdered: true,
        steps: isPublishComplete
          ? modelLibrarySteps.map((step) => ({
              ...step,
              isCompleted: true,
            }))
          : modelLibrarySteps,
      }}
      title={intl.formatMessage({
        id: 'ModelAddScope.title',
        defaultMessage: '2. Add Scope',
        description: 'Modal title for add scope step',
      })}
    >
      <Section
        title={
          isSingleCategory
            ? intl.formatMessage({
                id: 'ModelAddScope.setScopeTitleSingular',
                defaultMessage: 'Set scope',
                description:
                  'Section title for setting scope for a single model',
              })
            : intl.formatMessage({
                id: 'ModelAddScope.setScopeTitle',
                defaultMessage: 'Set scope for all categories',
                description: 'Section title for setting scope',
              })
        }
        description={
          isSingleCategory
            ? intl.formatMessage({
                id: 'ModelAddScope.setScopeDescriptionSingular',
                defaultMessage:
                  'Use filters to choose which documents the model will run on.',
                description:
                  'Description for scope setting options for a single model',
              })
            : intl.formatMessage({
                id: 'ModelAddScope.setScopeDescription',
                defaultMessage:
                  'Choose between setting scope for all categories or setting separate scopes for each category.',
                description: 'Description for scope setting options',
              })
        }
      >
        <Layout preset="sections">
          {!isSingleCategory && (
            <CardSelect
              direction="row"
              mode="bordered"
              name="scope"
              isMulti={false}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'ModelAddScope.globalScopeLabel',
                    defaultMessage: 'Apply to All',
                    description:
                      'Label for option to apply scope to all categories',
                  }),
                  description: intl.formatMessage({
                    id: 'ModelAddScope.globalScopeDescription',
                    defaultMessage:
                      'Apply filters to all categories to choose which documents the models will run on.',
                    description:
                      'Description for option to apply scope to all categories.',
                  }),
                  value: 'global',
                  isActive: scopeType === 'global',
                  width: '50%',
                },
                {
                  label: intl.formatMessage({
                    id: 'ModelAddScope.customScopeLabel',
                    defaultMessage: 'Set Custom Scope',
                    description: 'Label for custom scope option',
                  }),
                  description: intl.formatMessage({
                    id: 'ModelAddScope.customScopeDescription',
                    defaultMessage: 'Define scope for each category.',
                    description: 'Description for custom scope option',
                  }),
                  value: 'custom',
                  isActive: scopeType === 'custom',
                  width: '50%',
                },
              ]}
              onChange={(value) => setScopeType(value as ScopeType)}
              value={scopeType}
            />
          )}
          {!isSingleCategory && <Divider />}
          {scopeType === 'global' && (
            <GlobalScope hideTitle={isSingleCategory} />
          )}
          {scopeType === 'custom' && <CategoryScope />}
        </Layout>
      </Section>
      <ModelPreview hideActions />
      {confirmDraft}
      {confirmPublish}
      {publishingModal}
    </FullPageOverlay>
  );
};
